import React from 'react'
import { Provider } from 'react-redux'
import { ThemeProvider } from 'styled-components'
import { ParallaxProvider } from 'react-scroll-parallax'
import { datadogRum } from '@datadog/browser-rum'

// Polyfills:
import 'url-search-params-polyfill'

import createStore from './src/state/createStore'
import { saveState } from './src/state/localStorage'
import { createCookie } from './src/utils/cookie'
import { getAffiliate } from './src/utils/affiliates'
import { initDatadogRUM } from './src/utils/logging'
import { boot, update } from './src/utils/intercom'
import api from './src/utils/api'
import { FEATURES } from './src/config'
import { setDmpgObject, attach } from './src/static/scripts/dmpg'
import Layout from './src/components/Layout'
import { theme } from './src/constants/theme'
import CookieConsent from './src/components/CookieConsent'

const HIDE_INTERCOM_PATHS = [
  new RegExp(/^\/friend(\/)*$/),
  new RegExp(/^\/$/gi),
]

const store = createStore(true)
// Save auth info to localeStorage on each change
store.subscribe(() => saveState(store.getState()))

const scrollTo = (id) => () => {
  const el = document.querySelector(id)
  if (el) return window.scrollTo(0, el.offsetTop - 20)
  return false
}

const activate = () => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'optimize.activate' })
  }
}

export const onRouteUpdate = ({ location }) => {
  activate()

  datadogRum.startView(location.href)

  const { hash } = location
  if (hash) {
    window.setTimeout(scrollTo(hash), 10)
  }
}

export const onPreRouteUpdate = ({ location }) => {
  // Toggling Intercom depending on route
  for (let i = 0; i < HIDE_INTERCOM_PATHS.length; i += 1) {
    if (HIDE_INTERCOM_PATHS[i].test(location.pathname)) {
      return update({ hide_default_launcher: true })
    }
  }

  return update({ hide_default_launcher: false })
}

export const onClientEntry = async () => {
  const urlSearch = window.location.search
  const { referrer } = document
  const affiliate = getAffiliate({ urlSearch, referrer })

  if (affiliate) {
    createCookie({ name: 'affiliate', value: affiliate, days: 30 })
  }

  // Start Intercom
  boot({ hide_default_launcher: true })

  setDmpgObject()
  attach(document, window.smDl)

  try {
    const {
      data: { data },
    } = await api('/app_version')
    if (data.version) {
      initDatadogRUM(data.version.substr(7))
    }
  } catch (error) {
    console.log(error)
  }
}

export const wrapRootElement = ({ element }) => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <ParallaxProvider>{element}</ParallaxProvider>
    </ThemeProvider>
  </Provider>
)

// props provide same data to Layout as Page element will get
// including location, data, etc - you don't need to pass it
export const wrapPageElement = ({ element, props }) => (
  <React.Fragment>
    <Layout {...props}>{element}</Layout>
    {!FEATURES.ONE_TRUST_COOKIE_POLICY && <CookieConsent />}
  </React.Fragment>
)
